import {onBeforeUnmount, onMounted} from 'vue';
import {useUserStore} from '@navbar-logged/features/user';
import {useLogger} from '#imports';
import {sse, type SSESubscription} from '@malt/sse';
import {useLegalAgreementAcceptanceStore} from './legal-agreement-acceptance.store';
import {navbarEventBus} from '../../../events';

const ACCEPTANCE_REQUIREMENT_SSE_TYPE = 'umbrella-commercial-contract-acceptance';
const WATCH_TIMEOUT_MS = 30_000;

function normalizeRegistrationNumber(registrationNumber: string): string {
    return registrationNumber.replace(/\s/g, '').toUpperCase();
}

function isPushForRegistrationNumber(message: unknown, registrationNumber: string): boolean {
    return (
        typeof message === 'object' &&
        message !== null &&
        'registrationNumber' in message &&
        typeof message.registrationNumber === 'string' &&
        normalizeRegistrationNumber(message.registrationNumber) === normalizeRegistrationNumber(registrationNumber)
    );
}

export function useAcceptanceRequirementPush() {
    const userStore = useUserStore();
    const logger = useLogger();
    let subscription: SSESubscription | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let watchedRegistrationNumber: string | undefined;

    function stopWatching() {
        subscription?.unsubscribe();
        subscription = undefined;
        clearTimeout(timeout);
        timeout = undefined;
        watchedRegistrationNumber = undefined;
    }

    async function onMessage(message: unknown) {
        // The user fetched on connection may still reflect the previous acceptance: only the push for the saved number counts.
        if (!watchedRegistrationNumber || !isPushForRegistrationNumber(message, watchedRegistrationNumber)) {
            return;
        }
        try {
            await userStore.refreshUser();
        } catch (error) {
            logger.warn('Could not refresh the umbrella commercial contract acceptance requirement', error);
            return;
        }
        // The same version may have been accepted earlier in this session for the previous registration number.
        useLegalAgreementAcceptanceStore().forgetAcceptance();
        stopWatching();
    }

    function watchAcceptanceRequirement({registrationNumber}: {registrationNumber: string}) {
        if (!userStore.selectedIdentity?.umbrellaCommercialContractAcceptance) {
            return;
        }
        stopWatching();
        watchedRegistrationNumber = registrationNumber;
        subscription = sse.subscribe({eventType: ACCEPTANCE_REQUIREMENT_SSE_TYPE, url: '/navbar/api/user'}, onMessage);
        timeout = setTimeout(stopWatching, WATCH_TIMEOUT_MS);
    }

    onMounted(() => navbarEventBus.on('request:navbar-umbrella-contract-acceptance-watch', watchAcceptanceRequirement));

    onBeforeUnmount(() => {
        navbarEventBus.off('request:navbar-umbrella-contract-acceptance-watch', watchAcceptanceRequirement);
        stopWatching();
    });
}
